import JsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import {
  JEZIK_IZVESTAJA,
  TIP_IZVESTAJA,
  ucitajFontove,
  ucitajLogo,
} from './generisiIzvestajPDF';

const FONT = 'Roboto';
const LEVO = 20;
const SIRINA = 171;
const DESNO = LEVO + SIRINA;
const VISINA_STRANE = 297;
const DONJA_MARGINA = 25;
const LOGO_SIRINA = 36;
const LOGO_VISINA = (LOGO_SIRINA * 227) / 916;
const VISINA_LINIJE = 4.2;
const SIVA = [214, 214, 214];
const NARANDZASTA = [255, 170, 90];
const OZNAKA_OBRASCA = 'Z_RO01_06rev0';

export const PREVODI_WORK_REPORT = {
  SRB: {
    lokal: 'sr-RS',
    naslov: 'RADNI NALOG',
    broj: 'BROJ',
    datum: 'DATUM',
    kupac: 'KUPAC',
    robot: 'ROBOT',
    garancija: 'GARANCIJA',
    trazio: 'ZAHTEVAO',
    opisKvara: 'OPIS KVARA',
    radovi: 'IZVRŠENI RADOVI',
    oprema: 'SPISAK UTROŠENE OPREME',
    tehnicar: 'Serviser',
    sati: 'Ukupno radnih sati',
    satiPuta: 'Vreme putovanja (sati)',
    cenaSata: 'Cena po satu',
    cenaPuta: 'Cena putovanja po satu',
    naziv: 'Naziv',
    kolicina: 'Količina',
    cena: 'Cena',
    iznos: 'Iznos',
    rad: 'Rad',
    putovanje: 'Putovanje',
    delovi: 'Oprema',
    ukupno: 'Ukupno',
    napomena: 'NAPOMENA',
    potpisKupca: 'Potpis kupca',
    potpisTehnicara: 'Potpis servisera',
    da: 'Da',
    ne: 'Ne',
    mejlNaslov: (broj, datum) => `Izveštaj o radu ${broj} - ${datum}`,
    mejlTekst: (broj, datum) =>
      `Poštovani,\n\nU prilogu Vam dostavljamo izveštaj o radu za radni nalog ${broj} od ${datum}.\n\nSrdačan pozdrav,\nINMOLD CUSTOMER SERVICE TEAM`,
  },
  ENG: {
    lokal: 'en-GB',
    naslov: 'WORK ORDER',
    broj: 'NUMBER',
    datum: 'DATE',
    kupac: 'CUSTOMER',
    robot: 'ROBOT',
    garancija: 'WARRANTY',
    trazio: 'REQUESTED BY',
    opisKvara: 'FAILURE DESCRIPTION',
    radovi: 'WORK COMPLETED',
    oprema: 'LIST OF EQUIPMENT USED',
    tehnicar: 'Field Service Technician',
    sati: 'Total hours worked',
    satiPuta: 'Travel time (hours)',
    cenaSata: 'Price per hour',
    cenaPuta: 'Travel price per hour',
    naziv: 'Description',
    kolicina: 'Quantity',
    cena: 'Price',
    iznos: 'Amount',
    rad: 'Labour',
    putovanje: 'Travel',
    delovi: 'Equipment',
    ukupno: 'Total',
    napomena: 'NOTE',
    potpisKupca: "Customer's signature",
    potpisTehnicara: "Service technician's signature",
    da: 'Yes',
    ne: 'No',
    mejlNaslov: (broj, datum) => `Work report ${broj} - ${datum}`,
    mejlTekst: (broj, datum) =>
      `Dear Customer,\n\nAttached is the work report for work order ${broj} dated ${datum}.\n\nBest regards,\nINMOLD CUSTOMER SERVICE TEAM`,
  },
};

const tekst = (vrednost) =>
  vrednost === null || vrednost === undefined ? '' : String(vrednost).trim();
const broj = (vrednost) => Number(vrednost) || 0;
const zaokruzi = (vrednost) => Math.round(vrednost * 100) / 100;

const dvaMesta = (vrednost) => String(vrednost).padStart(2, '0');

// Dan u obliku yyyy-MM-dd (vrednost input type="date")
export const danIzVrednosti = (vrednost) => {
  if (!vrednost) return '';
  if (typeof vrednost === 'string' && /^\d{4}-\d{2}-\d{2}/.test(vrednost)) {
    return vrednost.slice(0, 10);
  }
  const datum = new Date(vrednost);
  if (Number.isNaN(datum.getTime())) return '';
  return `${datum.getFullYear()}-${dvaMesta(datum.getMonth() + 1)}-${dvaMesta(
    datum.getDate(),
  )}`;
};

export const danasnjiDan = () => danIzVrednosti(new Date());

export const prikazDana = (dan) => {
  const [godina, mesec, danUMesecu] = tekst(dan).split('-');
  return godina && mesec && danUMesecu
    ? `${danUMesecu}.${mesec}.${godina}`
    : '';
};

// Garancija važi od datuma početka garancije mašine narednih trajanjeGarancije meseci
const uGaranciji = (dan, pocetak, meseci) => {
  const pocetniDan = danIzVrednosti(pocetak);
  const brojMeseci = Number(meseci);
  if (!pocetniDan || !brojMeseci) return false;
  const [godina, mesec, danUMesecu] = pocetniDan.split('-').map(Number);
  const krajnjiDan = danIzVrednosti(
    new Date(godina, mesec - 1 + brojMeseci, danUMesecu),
  );
  return dan >= pocetniDan && dan < krajnjiDan;
};

// Naziv kupca na nalogu može već da sadrži adresu
const nazivBezAdrese = (naziv, adresa) => {
  const pozicija = adresa
    ? naziv.toLowerCase().indexOf(adresa.toLowerCase())
    : -1;
  return pozicija > 0
    ? naziv.slice(0, pozicija).replace(/[\s,;-]+$/, '')
    : naziv;
};

let brojacRedova = 0;
const kljucReda = () => {
  brojacRedova += 1;
  return `wr-${brojacRedova}`;
};

export const noviRadWorkReporta = (izvor = {}) => ({
  kljucReda: kljucReda(),
  datum: tekst(izvor.datum),
  tehnicar: tekst(izvor.tehnicar),
  tekst: tekst(izvor.tekst),
});

export const novaOpremaWorkReporta = (izvor = {}) => ({
  kljucReda: kljucReda(),
  naziv: tekst(izvor.naziv),
  kolicina: izvor.kolicina ?? 1,
  cena: izvor.cena ?? 0,
});

export const noviTehnicarWorkReporta = (izvor = {}) => ({
  kljucReda: kljucReda(),
  tehnicar: tekst(izvor.tehnicar),
  sati: izvor.sati ?? 0,
  satiPuta: izvor.satiPuta ?? 0,
  cenaSata: izvor.cenaSata ?? 0,
  cenaPuta: izvor.cenaPuta ?? 0,
});

export const iznosTehnicara = (red) =>
  broj(red.sati) * broj(red.cenaSata) + broj(red.satiPuta) * broj(red.cenaPuta);
export const iznosOpreme = (red) => broj(red.kolicina) * broj(red.cena);

export const ukupnoWorkReporta = (izvestaj) => {
  const zbir = (vrednost) =>
    izvestaj.tehnicari.reduce((acc, red) => acc + vrednost(red), 0);
  const satiRada = zbir((red) => broj(red.sati));
  const satiPuta = zbir((red) => broj(red.satiPuta));
  const rad = zbir((red) => broj(red.sati) * broj(red.cenaSata));
  const putovanje = zbir((red) => broj(red.satiPuta) * broj(red.cenaPuta));
  const delovi = izvestaj.oprema.reduce(
    (acc, red) => acc + iznosOpreme(red),
    0,
  );
  return {
    satiRada,
    satiPuta,
    rad,
    putovanje,
    delovi,
    ukupno: rad + putovanje + delovi,
  };
};

const POLJE_CENE = {
  delovi: 'nabavnaCena',
};

const prvaStavka = (cenovnik) => cenovnik[0];
const najskupljaStavka = (cenovnik) =>
  cenovnik.reduce((max, stavka) =>
    broj(stavka.cena) > broj(max.cena) ? stavka : max,
  );

// Cena iz cenovnika (_VrsteUsluge) za vrstu usluge koju je zaposleni najviše koristio;
// ako je nije koristio, rezervna stavka cenovnika
const cenaIzCenovnika = (cenovnik, redovi, rezervnaStavka) => {
  if (!cenovnik?.length) return 0;
  const satiPoVrsti = redovi.reduce(
    (acc, red) => ({
      ...acc,
      [red.idVrstaUsluge]: (acc[red.idVrstaUsluge] || 0) + broj(red.kolicina),
    }),
    {},
  );
  const najvise = Object.keys(satiPoVrsti).sort(
    (a, b) => satiPoVrsti[b] - satiPoVrsti[a],
  )[0];
  const stavka =
    cenovnik.find((s) => String(s.id) === najvise) || rezervnaStavka(cenovnik);
  return zaokruzi(broj(stavka?.cena));
};

// Svi zaposleni na nalogu; sati samo za izabrani dan. Cena po satu je iz cenovnika usluga
// za tog zaposlenog i kupca (tehničar/inženjer, domaći/ino)
const tehnicariZaDan = (
  zaposleni,
  usluge,
  transport,
  istiDan,
  ceneUsluga = {},
) => {
  const idPoImenu = {};
  zaposleni.forEach((red) => {
    idPoImenu[tekst(red.vrednost || red.zaposleni)] = red.id;
  });
  [...usluge, ...transport].forEach((red) => {
    const ime = tekst(red.zaposleni);
    if (!idPoImenu[ime]) idPoImenu[ime] = red.idZaposlenog;
  });
  const imena = [
    ...zaposleni.map((red) => tekst(red.vrednost || red.zaposleni)),
    ...usluge.map((red) => tekst(red.zaposleni)),
    ...transport.map((red) => tekst(red.zaposleni)),
  ].filter((ime, index, sva) => ime && sva.indexOf(ime) === index);

  return imena.map((ime) => {
    const zaIme = (redovi) =>
      redovi.filter((red) => tekst(red.zaposleni) === ime);
    const rad = zaIme(usluge);
    const put = zaIme(transport);
    const radDana = rad.filter(istiDan);
    const putDana = put.filter(istiDan);
    const cenovnik = ceneUsluga[idPoImenu[ime]] || {};
    return noviTehnicarWorkReporta({
      tehnicar: ime,
      sati: zaokruzi(radDana.reduce((acc, red) => acc + broj(red.kolicina), 0)),
      satiPuta: zaokruzi(
        putDana.reduce((acc, red) => acc + broj(red.kolicina), 0),
      ),
      cenaSata: cenaIzCenovnika(
        cenovnik.rad,
        radDana.length ? radDana : rad,
        prvaStavka,
      ),
      cenaPuta: cenaIzCenovnika(
        cenovnik.put,
        putDana.length ? putDana : put,
        najskupljaStavka,
      ),
    });
  });
};

// Model work reporta za izabrani dan: sve što ide u PDF i što korisnik može da izmeni
export const napraviWorkReport = ({ podaci, datum, dozvoljeneCene }) => {
  const { zaglavlje, zahtev, kupac } = podaci;
  const dan = danIzVrednosti(datum) || danasnjiDan();
  const istiDan = (red) => danIzVrednosti(red.datum) === dan;
  const robot = [zaglavlje.nazivMasine, zaglavlje.nazivPodProjekta]
    .map(tekst)
    .filter(Boolean)
    .join(' - ');
  const adresaKupca = tekst(kupac?.adresa);

  return {
    workReport: true,
    tip: TIP_IZVESTAJA.RN,
    dozvoljeneCene: Boolean(dozvoljeneCene),
    prikaziCene: Boolean(dozvoljeneCene),
    prikaziZbirCena: true,
    datum: dan,
    broj: tekst(zaglavlje.ceoBrDok),
    kupac: nazivBezAdrese(
      tekst(kupac?.nazivFirme || zaglavlje.nazivKupca),
      adresaKupca,
    ),
    adresaKupca,
    robot,
    garancija: uGaranciji(
      dan,
      zaglavlje.datumKupovine,
      zaglavlje.trajanjeGarancije,
    ),
    trazio: tekst(zahtev?.imeKorisnikaSlanjaZahteva),
    opisKvara: tekst(zaglavlje.opisProblema || zahtev?.opisProblema),
    radovi: (podaci.dnevnikTeren || []).filter(istiDan).map((red) =>
      noviRadWorkReporta({
        datum: prikazDana(dan),
        tehnicar: red.imeZaposlenog,
        tekst: [red.izvrsenaRadnja, red.napomena]
          .map(tekst)
          .filter(Boolean)
          .join('\n'),
      }),
    ),
    oprema: (podaci.delovi || []).map((red) =>
      novaOpremaWorkReporta({
        naziv: red.nazivDela,
        kolicina: broj(red.kolicina),
        cena: broj(red[POLJE_CENE.delovi]),
      }),
    ),
    tehnicari: tehnicariZaDan(
      podaci.zaposleni || [],
      podaci.usluge || [],
      podaci.transport || [],
      istiDan,
      podaci.ceneUsluga,
    ),
    napomena: '',
  };
};

export const prevodWorkReporta = (jezik) => {
  const prevod = PREVODI_WORK_REPORT[jezik] || PREVODI_WORK_REPORT.SRB;
  return {
    ...prevod,
    daNe: (vrednost) => (vrednost ? prevod.da : prevod.ne),
    formatBroj: (vrednost) =>
      Number(vrednost || 0).toLocaleString(prevod.lokal, {
        style: 'currency',
        currency: 'EUR',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
    formatSati: (vrednost) =>
      Number(vrednost || 0).toLocaleString(prevod.lokal, {
        maximumFractionDigits: 2,
      }),
  };
};

export const podrazumevaniMejlWorkReporta = (jezik, brojNaloga, dan) => {
  const p = prevodWorkReporta(jezik);
  return {
    naslov: p.mejlNaslov(tekst(brojNaloga), prikazDana(dan)),
    tekst: p.mejlTekst(tekst(brojNaloga), prikazDana(dan)),
  };
};

export const nazivFajlaWorkReporta = (izvestaj, jezik) =>
  `WorkReport_${izvestaj.broj || 'RN'}_${izvestaj.datum}${
    jezik === JEZIK_IZVESTAJA.ENG ? '_ENG' : ''
  }.pdf`;

const STIL = {
  theme: 'grid',
  margin: { left: LEVO, right: LEVO, top: 15, bottom: DONJA_MARGINA },
  styles: {
    font: FONT,
    fontSize: 10,
    textColor: 0,
    lineColor: 0,
    lineWidth: 0.3,
    cellPadding: 1.5,
    overflow: 'linebreak',
  },
  headStyles: {
    font: FONT,
    fontStyle: 'bold',
    fontSize: 9,
    fillColor: 255,
    textColor: 0,
    halign: 'center',
  },
  footStyles: {
    font: FONT,
    fontStyle: 'bold',
    fillColor: [240, 240, 240],
    textColor: 0,
  },
};

const labela = (doc, vrednost, x, y) => {
  doc.setFont(FONT, 'bold');
  doc.setFontSize(10);
  doc.text(`${vrednost}:`, x, y);
};

const linijeOkvira = (doc, vrednosti, sirina) => {
  doc.setFont(FONT, 'normal');
  doc.setFontSize(10);
  return vrednosti
    .map(tekst)
    .filter(Boolean)
    .flatMap((vrednost) => doc.splitTextToSize(vrednost, sirina - 3));
};

const visinaOkvira = (linije) =>
  Math.max(10, linije.length * VISINA_LINIJE + 3);

const okvir = (doc, x, y, sirina, visina, linije, centar = false) => {
  doc.setDrawColor(0);
  doc.setLineWidth(0.3);
  doc.rect(x, y, sirina, visina);
  doc.setFont(FONT, 'normal');
  doc.setFontSize(10);
  const pocetak =
    y + visina / 2 - ((linije.length - 1) * VISINA_LINIJE) / 2 + 1.3;
  linije.forEach((linija, index) =>
    doc.text(
      linija,
      centar ? x + sirina / 2 : x + 1.5,
      pocetak + index * VISINA_LINIJE,
      centar ? { align: 'center' } : undefined,
    ),
  );
};

const proveriPrelom = (doc, stanje, potrebnaVisina) => {
  if (stanje.y + potrebnaVisina > VISINA_STRANE - DONJA_MARGINA) {
    doc.addPage();
    stanje.y = 20;
  }
};

const naslovSekcije = (doc, stanje, naslov) => {
  proveriPrelom(doc, stanje, 28);
  doc.setFont(FONT, 'bold');
  doc.setFontSize(14);
  doc.setTextColor(0);
  doc.text(naslov, LEVO, stanje.y);
  stanje.y += 2;
};

const tabela = (doc, stanje, opcije) => {
  autoTable(doc, { ...STIL, startY: stanje.y, ...opcije });
  stanje.y = doc.lastAutoTable.finalY + 8;
};

const prazanOkvir = (visina) => [
  [{ content: '', styles: { minCellHeight: visina } }],
];

const dodajZaglavlje = (doc, stanje, p, izvestaj, logo) => {
  if (logo) {
    doc.addImage(
      logo,
      'PNG',
      LEVO,
      12,
      LOGO_SIRINA,
      LOGO_VISINA,
      'logo',
      'FAST',
    );
  }
  doc.setFont(FONT, 'bold');
  doc.setFontSize(8);
  doc.setTextColor(0);
  doc.text('INMOLD GROUP d.o.o.', LEVO, 29);
  doc.text('Knjaza Miloša 70, 31210 Požega, Serbia', LEVO, 34.5);

  const xLabele = 91;
  const xVrednosti = 118;
  const sirinaVrednosti = DESNO - xVrednosti;
  let y = 40;

  doc.setFillColor(...SIVA);
  doc.rect(LEVO, y, 50, 10, 'F');
  doc.setFontSize(15);
  doc.text(p.naslov, LEVO + 1.5, y + 7.3);
  labela(doc, p.broj, xLabele, y + 6.5);
  okvir(doc, xVrednosti, y, sirinaVrednosti, 10, [izvestaj.broj]);

  y += 13;
  const linijeKupca = linijeOkvira(
    doc,
    [izvestaj.kupac, izvestaj.adresaKupca],
    sirinaVrednosti,
  );
  const visinaKupca = visinaOkvira(linijeKupca);
  labela(doc, p.datum, LEVO, y + visinaKupca / 2 + 1.3);
  okvir(
    doc,
    50,
    y + (visinaKupca - 10) / 2,
    24,
    10,
    [prikazDana(izvestaj.datum)],
    true,
  );
  labela(doc, p.kupac, xLabele, y + visinaKupca / 2 + 1.3);
  okvir(doc, xVrednosti, y, sirinaVrednosti, visinaKupca, linijeKupca);

  y += visinaKupca + 3;
  const linijeRobota = linijeOkvira(doc, [izvestaj.robot], DESNO - 50);
  const visinaRobota = visinaOkvira(linijeRobota);
  labela(doc, p.robot, LEVO, y + visinaRobota / 2 + 1.3);
  okvir(doc, 50, y, DESNO - 50, visinaRobota, linijeRobota);

  y += visinaRobota + 3;
  const linijeTrazio = linijeOkvira(doc, [izvestaj.trazio], sirinaVrednosti);
  const visinaTrazio = visinaOkvira(linijeTrazio);
  labela(doc, p.garancija, LEVO, y + visinaTrazio / 2 + 1.3);
  okvir(
    doc,
    50,
    y + (visinaTrazio - 10) / 2,
    24,
    10,
    [p.daNe(izvestaj.garancija)],
    true,
  );
  labela(doc, p.trazio, 81, y + visinaTrazio / 2 + 1.3);
  okvir(doc, xVrednosti, y, sirinaVrednosti, visinaTrazio, linijeTrazio);

  stanje.y = y + visinaTrazio + 10;
};

const dodajOpremu = (doc, stanje, p, izvestaj, cene) => {
  naslovSekcije(doc, stanje, p.oprema);
  if (!izvestaj.oprema.length) {
    tabela(doc, stanje, { body: prazanOkvir(35) });
    return;
  }
  tabela(doc, stanje, {
    head: [[p.naziv, p.kolicina, ...(cene ? [p.cena, p.iznos] : [])]],
    body: izvestaj.oprema.map((red) => [
      tekst(red.naziv),
      p.formatSati(red.kolicina),
      ...(cene ? [p.formatBroj(red.cena), p.formatBroj(iznosOpreme(red))] : []),
    ]),
    columnStyles: {
      1: { halign: 'center', cellWidth: 25 },
      2: { halign: 'right', cellWidth: 30 },
      3: { halign: 'right', cellWidth: 30 },
    },
  });
};

const dodajTehnicare = (doc, stanje, p, izvestaj, cene) => {
  const { tehnicari } = izvestaj;
  proveriPrelom(doc, stanje, 20);
  const ukupnoSati = tehnicari.reduce((acc, red) => acc + broj(red.sati), 0);
  const ukupnoPuta = tehnicari.reduce(
    (acc, red) => acc + broj(red.satiPuta),
    0,
  );
  tabela(doc, stanje, {
    head: [
      [
        p.tehnicar,
        p.sati,
        p.satiPuta,
        ...(cene ? [p.cenaSata, p.cenaPuta, p.iznos] : []),
      ],
    ],
    body: tehnicari.map((red) => {
      const sati = broj(red.sati);
      const satiPuta = broj(red.satiPuta);
      return [
        tekst(red.tehnicar),
        sati ? p.formatSati(sati) : '',
        satiPuta ? p.formatSati(satiPuta) : '',
        ...(cene
          ? [
              sati ? p.formatBroj(red.cenaSata) : '',
              satiPuta ? p.formatBroj(red.cenaPuta) : '',
              sati || satiPuta ? p.formatBroj(iznosTehnicara(red)) : '',
            ]
          : []),
      ];
    }),
    foot:
      tehnicari.length > 1 || cene
        ? [
            [
              p.ukupno,
              p.formatSati(ukupnoSati),
              p.formatSati(ukupnoPuta),
              ...(cene
                ? [
                    '',
                    '',
                    p.formatBroj(
                      tehnicari.reduce(
                        (acc, red) => acc + iznosTehnicara(red),
                        0,
                      ),
                    ),
                  ]
                : []),
            ],
          ]
        : undefined,
    showFoot: 'lastPage',
    styles: { ...STIL.styles, halign: 'center' },
    footStyles: { ...STIL.footStyles, halign: 'center' },
    columnStyles: cene
      ? { 0: { cellWidth: 44 } }
      : { 0: { cellWidth: 80 }, 1: { cellWidth: 55 } },
  });
};

const dodajUkupno = (doc, stanje, p, izvestaj) => {
  const { satiRada, satiPuta, rad, putovanje, delovi, ukupno } =
    ukupnoWorkReporta(izvestaj);
  proveriPrelom(doc, stanje, 32);
  tabela(doc, stanje, {
    margin: { ...STIL.margin, left: DESNO - 90 },
    tableWidth: 90,
    body: [
      [`${p.rad} (${p.formatSati(satiRada)} h)`, p.formatBroj(rad)],
      [`${p.putovanje} (${p.formatSati(satiPuta)} h)`, p.formatBroj(putovanje)],
      [p.delovi, p.formatBroj(delovi)],
      [
        {
          content: p.ukupno,
          styles: { fontStyle: 'bold', fillColor: [240, 240, 240] },
        },
        {
          content: p.formatBroj(ukupno),
          styles: { fontStyle: 'bold', fillColor: [240, 240, 240] },
        },
      ],
    ],
    columnStyles: { 1: { halign: 'right' } },
  });
};

const dodajPotpise = (doc, stanje, p) => {
  const vrhPotpisa = VISINA_STRANE - DONJA_MARGINA - 20;
  if (stanje.y > vrhPotpisa) doc.addPage();
  const sirinaLinije = 75;
  doc.setFont(FONT, 'bold');
  doc.setFontSize(8);
  doc.setTextColor(0);
  doc.text(p.potpisKupca, LEVO + sirinaLinije / 2, vrhPotpisa + 3, {
    align: 'center',
  });
  doc.text(p.potpisTehnicara, DESNO - sirinaLinije / 2, vrhPotpisa + 3, {
    align: 'center',
  });
  doc.setDrawColor(0);
  doc.setLineWidth(0.3);
  doc.line(LEVO, vrhPotpisa + 17, LEVO + sirinaLinije, vrhPotpisa + 17);
  doc.line(DESNO - sirinaLinije, vrhPotpisa + 17, DESNO, vrhPotpisa + 17);
};

const dodajPodnozje = (doc) => {
  const brojStrana = doc.getNumberOfPages();
  for (let i = 1; i <= brojStrana; i += 1) {
    doc.setPage(i);
    doc.setFillColor(...NARANDZASTA);
    doc.rect(LEVO, VISINA_STRANE - 20, SIRINA, 5, 'F');
    doc.setFont(FONT, 'bold');
    doc.setFontSize(8);
    doc.setTextColor(255);
    doc.text('WWW.INMOLD-LTD.COM', LEVO + SIRINA / 2, VISINA_STRANE - 16.4, {
      align: 'center',
    });
    doc.setFont(FONT, 'normal');
    doc.setTextColor(0);
    doc.text(OZNAKA_OBRASCA, DESNO, VISINA_STRANE - 9.5, { align: 'right' });
    doc.text(`${i} / ${brojStrana}`, LEVO, VISINA_STRANE - 9.5);
  }
};

export const generisiWorkReportPDF = async ({
  izvestaj,
  jezik = JEZIK_IZVESTAJA.SRB,
}) => {
  const p = prevodWorkReporta(jezik);
  const cene = Boolean(izvestaj.dozvoljeneCene && izvestaj.prikaziCene);
  const [{ regular, bold }, logo] = await Promise.all([
    ucitajFontove(),
    ucitajLogo(),
  ]);
  const doc = new JsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  doc.addFileToVFS('Roboto-Regular.ttf', regular);
  doc.addFont('Roboto-Regular.ttf', FONT, 'normal');
  doc.addFileToVFS('Roboto-Medium.ttf', bold);
  doc.addFont('Roboto-Medium.ttf', FONT, 'bold');

  const stanje = { y: 20 };
  dodajZaglavlje(doc, stanje, p, izvestaj, logo);

  naslovSekcije(doc, stanje, p.opisKvara);
  tabela(doc, stanje, {
    body: [
      [{ content: tekst(izvestaj.opisKvara), styles: { minCellHeight: 36 } }],
    ],
  });

  naslovSekcije(doc, stanje, p.radovi);
  tabela(doc, stanje, {
    body: izvestaj.radovi.length
      ? izvestaj.radovi.map((red) => [
          [red.datum, red.tehnicar, red.tekst]
            .map(tekst)
            .filter(Boolean)
            .join('\n'),
        ])
      : prazanOkvir(30),
  });

  dodajOpremu(doc, stanje, p, izvestaj, cene);
  if (izvestaj.tehnicari.length) dodajTehnicare(doc, stanje, p, izvestaj, cene);
  if (cene && izvestaj.prikaziZbirCena !== false) {
    dodajUkupno(doc, stanje, p, izvestaj);
  }

  if (tekst(izvestaj.napomena)) {
    naslovSekcije(doc, stanje, p.napomena);
    tabela(doc, stanje, { body: [[tekst(izvestaj.napomena)]] });
  }

  dodajPotpise(doc, stanje, p);
  dodajPodnozje(doc);

  return { doc, nazivFajla: nazivFajlaWorkReporta(izvestaj, jezik) };
};
